import React, { useState, useEffect, useCallback } from 'react';
import { Rocket, CheckCircle2, Circle, ChevronRight, ExternalLink, Lock } from 'lucide-react';
import { carsysApi } from '../api/carsysApi';
import { useT } from '../context/LanguageContext';
import { buildStepMeta, openSettingsTab } from '../lib/onboardingSteps';

export default function AanDeSlag({ onNavigate }) {
  const { t } = useT();
  const [status, setStatus] = useState(null);

  const load = useCallback(() => { carsysApi.getOnboardingStatus().then(setStatus).catch(() => setStatus(null)); }, []);
  useEffect(() => { load(); }, [load]);

  const meta = buildStepMeta(t);
  const steps = status?.steps || [];
  const pct = status && status.applicableTotal ? Math.round((status.applicableDone / status.applicableTotal) * 100) : 0;

  return (
    <div className="cs-page" data-testid="aandeslag-page" style={{ padding: 24, maxWidth: 880 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <Rocket size={22} color="#2563eb" />
        <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#0f172a' }}>{t('Aan de slag', 'Aan de slag')}</h1>
      </div>
      <p style={{ color: '#64748b', margin: '0 0 18px', fontSize: 14 }}>
        {t('Volg deze stappen om MonteurCentraal volledig in te richten voor jouw garage.', 'Volg deze stappen om MonteurCentraal volledig in te richten voor jouw garage.')}
      </p>

      {status && status.applicableTotal > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, color: '#475569', marginBottom: 6, fontWeight: 600 }}>{status.applicableDone}/{status.applicableTotal} {t('voltooid', 'voltooid')}</div>
          <div style={{ height: 8, background: '#e2e8f0', borderRadius: 99, overflow: 'hidden' }}><div style={{ height: '100%', width: `${pct}%`, background: '#2563eb', transition: 'width 300ms ease' }} /></div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 12 }}>
        {steps.map(s => {
          const m = meta[s.key]; if (!m) return null;
          const Icon = m.icon;
          const locked = !s.applicable; // PRO-stap, maar garage is geen PRO
          return (
            <div key={s.key} data-testid={`guide-step-${s.key}`} style={{ ...card, opacity: locked ? 0.78 : 1 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ marginTop: 2 }}>
                  {locked ? <Lock size={20} color="#cbd5e1" /> : (s.done ? <CheckCircle2 size={22} color="#16a34a" /> : <Circle size={22} color="#cbd5e1" />)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <Icon size={17} color="#2563eb" />
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#0f172a' }}>{m.title}</span>
                    {m.proOnly && <span style={proBadge}>PRO</span>}
                    {!locked && s.done && <span style={doneBadge}>{t('Klaar', 'Klaar')}</span>}
                  </div>
                  <p style={{ color: '#475569', fontSize: 13.5, margin: '7px 0 10px', lineHeight: 1.5 }}>{m.desc}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                    {locked ? (
                      <button style={primaryBtn} data-testid={`guide-upgrade-${s.key}`} onClick={() => openSettingsTab(onNavigate, 'plannen')}>
                        {t('Upgrade naar PRO', 'Upgrade naar PRO')} <ChevronRight size={14} />
                      </button>
                    ) : (
                      <button style={primaryBtn} data-testid={`guide-go-${s.key}`} onClick={() => m.nav(onNavigate)}>
                        {s.done ? t('Bekijken', 'Bekijken') : t('Nu instellen', 'Nu instellen')} <ChevronRight size={14} />
                      </button>
                    )}
                    {m.link && (
                      <a href={m.link.url} target="_blank" rel="noreferrer" style={extLink} data-testid={`guide-link-${s.key}`}>
                        {m.link.label} <ExternalLink size={12} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const card = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, boxShadow: '0 2px 6px rgba(15,23,42,.04)' };
const primaryBtn = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '8px 14px', border: 'none', borderRadius: 8, background: '#2563eb', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const extLink = { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#2563eb', fontWeight: 600, fontSize: 12.5, textDecoration: 'none' };
const proBadge = { fontSize: 10, fontWeight: 800, background: '#fef3c7', color: '#92400e', padding: '2px 7px', borderRadius: 99, letterSpacing: 0.4 };
const doneBadge = { fontSize: 10, fontWeight: 800, background: '#dcfce7', color: '#166534', padding: '2px 7px', borderRadius: 99 };
